// src/services/xpEngine.ts
// Unified XP progression engine: event bus, XP calculation, level management.
// All user actions flow through here to award XP, check achievements, and advance skill tree.
//
//   ACTION (workout, analysis, check-in)
//       |
//       v
//   xpEngine.emit(userId, event)
//       |
//       +---> Write to progression_events table (fire-and-forget)
//       +---> Calculate XP (sync)
//       |       +---> Update profiles (total_xp, current_level)
//       |       +---> Notify subscribers (XP toast, level-up)
//       +---> Schedule achievement check (500ms debounce, async)

import { supabase } from '../lib/supabase';
import { checkAchievementUnlocks, type AchievementUnlockResult } from './achievementEngine';
import { track } from './heliosTelemetry';

// ── Types ────────────────────────────────────────────────────────────

export type XPEventType =
  | 'workout_logged'
  | 'analysis_complete'
  | 'checkin_done'
  | 'streak_updated'
  | 'skill_unlocked'
  | 'achievement_earned';

export interface XPEvent {
  type: XPEventType;
  payload: Record<string, unknown>;
}

export interface XPAward {
  amount: number;
  source: string;
  breakdown?: string;
}

export interface LevelUpResult {
  oldLevel: number;
  newLevel: number;
  oldTier: string;
  newTier: string;
}

export interface XPProgressionResult {
  xp: XPAward;
  levelUp: LevelUpResult | null;
  achievements: AchievementUnlockResult[];
}

export interface UserXPState {
  totalXp: number;
  level: number;
  tier: string;
  xpForCurrentLevel: number;
  xpForNextLevel: number;
  progressPercent: number;
}

// ── XP Level Curve ───────────────────────────────────────────────────
// level N requires floor(500 * N^1.4) cumulative XP
//
//   Level  1:     500 XP (~3 workouts)
//   Level  5:   4,787 XP (~2 weeks active)
//   Level 10:  12,559 XP (~6 weeks)
//   Level 25:  51,373 XP (~6 months)
//   Level 50: 147,912 XP (~18 months)

const MAX_LEVEL = 50;

export function xpForLevel(level: number): number {
  if (level <= 0) return 0;
  return Math.floor(500 * Math.pow(level, 1.4));
}

export function levelFromXP(totalXP: number): number {
  for (let lvl = MAX_LEVEL; lvl >= 1; lvl--) {
    if (totalXP >= xpForLevel(lvl)) return lvl;
  }
  return 0;
}

export function tierFromLevel(level: number): string {
  if (level >= 45) return 'Olympian';
  if (level >= 35) return 'Sculptor';
  if (level >= 25) return 'Architect';
  if (level >= 15) return 'Builder';
  if (level >= 5) return 'Apprentice';
  return 'Novice';
}

// ── XP Calculation ───────────────────────────────────────────────────

export function calculateXP(event: XPEvent): XPAward {
  switch (event.type) {
    case 'workout_logged': {
      const sets = typeof event.payload.totalSets === 'number' ? event.payload.totalSets : 0;
      const raw = 100 + sets * 10;
      const amount = Math.min(300, Math.max(0, raw));
      return { amount, source: 'Workout', breakdown: `100 base + ${sets} sets x 10` };
    }
    case 'analysis_complete':
      return { amount: 200, source: 'Analysis' };
    case 'checkin_done':
      return { amount: 50, source: 'Check-in' };
    case 'streak_updated': {
      const isMilestone = event.payload.isMilestone === true;
      return { amount: isMilestone ? 100 : 25, source: isMilestone ? 'Streak Milestone' : 'Streak Day' };
    }
    case 'skill_unlocked':
      return { amount: 100, source: 'Skill Unlock' };
    case 'achievement_earned':
      return { amount: 0, source: 'Achievement' };
    default:
      return { amount: 0, source: 'Unknown' };
  }
}

// ── Event Subscribers ────────────────────────────────────────────────

type XPSubscriber = (result: XPProgressionResult) => void;
const subscribers: XPSubscriber[] = [];

export function onXPEvent(callback: XPSubscriber): () => void {
  subscribers.push(callback);
  return () => {
    const idx = subscribers.indexOf(callback);
    if (idx >= 0) subscribers.splice(idx, 1);
  };
}

function notifySubscribers(result: XPProgressionResult): void {
  for (const sub of subscribers) {
    try { sub(result); } catch (err) {
      console.error('[xpEngine] subscriber error:', err);
    }
  }
}

// ── Core: Emit Event ─────────────────────────────────────────────────

let achievementCheckTimer: ReturnType<typeof setTimeout> | null = null;
let pendingUserId: string | null = null;

export async function emit(
  userId: string,
  event: XPEvent,
): Promise<XPProgressionResult> {
  const xp = calculateXP(event);

  // Track workout completion for dogfood observability
  if (event.type === 'workout_logged' && event.payload?.session_complete) {
    try {
      track('workout_completed', {
        xp_awarded: xp.amount,
        sets: event.payload?.sets_completed,
      });
    } catch { /* telemetry never throws */ }
  }

  // 1. Write event to ledger (fire-and-forget)
  supabase.from('progression_events').insert({
    user_id: userId,
    event_type: event.type,
    payload: event.payload,
    xp_awarded: xp.amount,
  }).then(({ error }) => {
    if (error) console.warn('[xpEngine] event ledger write failed:', error.message);
  });

  // 2. Update XP and check level
  let levelUp: LevelUpResult | null = null;
  if (xp.amount > 0) {
    levelUp = await updateXPAndLevel(userId, xp.amount);
  }

  // 3. Schedule achievement check (500ms debounce)
  pendingUserId = userId;
  if (achievementCheckTimer) clearTimeout(achievementCheckTimer);

  const achievementPromise = new Promise<AchievementUnlockResult[]>((resolve) => {
    achievementCheckTimer = setTimeout(async () => {
      if (pendingUserId !== userId) { resolve([]); return; }
      try {
        const unlocked = await checkAchievementUnlocks(userId);
        resolve(unlocked);
      } catch (err) {
        console.warn('[xpEngine] achievement check failed:', err);
        resolve([]);
      }
    }, 500);
  });

  // Notify immediately with XP result
  const result: XPProgressionResult = { xp, levelUp, achievements: [] };
  notifySubscribers(result);

  // When achievements resolve, notify again
  achievementPromise.then((achievements) => {
    if (achievements.length > 0) {
      notifySubscribers({ xp: { amount: 0, source: 'Achievement Check' }, levelUp: null, achievements });
    }
  });

  return result;
}

// ── Update XP & Level ────────────────────────────────────────────────

async function updateXPAndLevel(userId: string, xpGain: number): Promise<LevelUpResult | null> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('total_xp, current_level')
    .eq('id', userId)
    .single();

  const oldXP = profile?.total_xp ?? 0;
  const newXP = oldXP + xpGain;
  const oldLevel = profile?.current_level ?? levelFromXP(oldXP);
  const newLevel = levelFromXP(newXP);

  const { error } = await supabase
    .from('profiles')
    .update({ total_xp: newXP, current_level: newLevel })
    .eq('id', userId);

  if (error) {
    console.error('[xpEngine] XP update failed:', error.message);
    return null;
  }

  if (newLevel > oldLevel) {
    return {
      oldLevel,
      newLevel,
      oldTier: tierFromLevel(oldLevel),
      newTier: tierFromLevel(newLevel),
    };
  }
  return null;
}

// ── Get User XP State ────────────────────────────────────────────────

export async function getUserXPState(userId: string): Promise<UserXPState> {
  const { data } = await supabase
    .from('profiles')
    .select('total_xp, current_level')
    .eq('id', userId)
    .single();

  const totalXp = data?.total_xp ?? 0;
  const level = data?.current_level ?? levelFromXP(totalXp);
  const tier = tierFromLevel(level);
  const xpForCurrentLevel = xpForLevel(level);
  const xpForNextLevel = level < MAX_LEVEL ? xpForLevel(level + 1) : xpForCurrentLevel;
  const range = xpForNextLevel - xpForCurrentLevel;
  const progress = range > 0 ? totalXp - xpForCurrentLevel : 0;
  const progressPercent = range > 0 ? Math.min(100, Math.round((progress / range) * 100)) : 100;

  return { totalXp, level, tier, xpForCurrentLevel, xpForNextLevel, progressPercent };
}

// ── Retroactive Migration ────────────────────────────────────────────

export async function migrateExistingUser(userId: string): Promise<{ xp: number; level: number } | null> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('progression_migrated_at, total_xp')
    .eq('id', userId)
    .single();

  if (profile?.progression_migrated_at) return null;

  let totalXP = profile?.total_xp ?? 0;

  try {
    const [workouts, analyses, checkins, streakData] = await Promise.all([
      supabase.from('workout_logs').select('*', { count: 'exact', head: true }).eq('user_id', userId),
      supabase.from('analyses').select('*', { count: 'exact', head: true }).eq('user_id', userId),
      supabase.from('morning_checkins').select('*', { count: 'exact', head: true }).eq('user_id', userId),
      supabase.from('profiles').select('streak_count').eq('id', userId).single(),
    ]);

    totalXP += (workouts.count ?? 0) * 150;  // avg workout XP
    totalXP += (analyses.count ?? 0) * 200;
    totalXP += (checkins.count ?? 0) * 50;
    totalXP += (streakData.data?.streak_count ?? 0) * 25;

    const level = levelFromXP(totalXP);

    await supabase
      .from('profiles')
      .update({
        total_xp: totalXP,
        current_level: level,
        progression_migrated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    console.log(`[xpEngine] Migrated user ${userId}: ${totalXP} XP, Level ${level}`);
    return { xp: totalXP, level };
  } catch (err) {
    console.error('[xpEngine] Migration failed:', err);
    return null;
  }
}
